import React, {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { cn } from './model/derive';

/**
 * 2days 드롭다운 — <select> 처럼 option·optgroup 을 받고, 목록은 body 에 띄워 모달에 잘리지 않는다.
 * option 에 data-sub 를 주면 목록에서 이름 아래 둘째 줄(최대 2줄)로 보인다 — 예: 댓글 고르기의 댓글 내용
 */

const GAP = 4;
const EDGE = 16;
const MAX_H = 280;

const textOf = (node) =>
  Children.toArray(node)
    .map((c) =>
      typeof c === 'string' || typeof c === 'number'
        ? String(c)
        : isValidElement(c)
          ? textOf(c.props.children)
          : '',
    )
    .join('');

const collect = (children, group = null, out = []) => {
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return;
    if (c.type === Fragment) collect(c.props.children, group, out);
    else if (c.type === 'optgroup') collect(c.props.children, c.props.label, out);
    else if (c.type === 'option') {
      const label = textOf(c.props.children);
      out.push({
        value: String(c.props.value ?? label),
        label,
        sub: c.props['data-sub'] || null,
        disabled: !!c.props.disabled,
        group,
      });
    }
  });
  return out;
};

const TdSelect = ({
  id,
  value,
  onChange,
  disabled,
  className,
  children,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
}) => {
  const options = collect(children);
  const selected = String(value ?? '');
  const currentIndex = options.findIndex((o) => o.value === selected);
  const current = options[currentIndex];
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);

  const close = (focus) => {
    setOpen(false);
    if (focus) buttonRef.current?.focus();
  };
  const openList = () => {
    if (disabled) return;
    setActive(currentIndex >= 0 && !options[currentIndex].disabled ? currentIndex : (enabled[0] ?? -1));
    setOpen(true);
  };
  const pick = (o) => {
    if (!o || o.disabled) return;
    if (o.value !== selected) {
      onChange?.({ target: { value: o.value, id }, currentTarget: { value: o.value, id } });
    }
    close(true);
  };
  const step = (dir) => {
    if (!enabled.length) return;
    const at = enabled.indexOf(active);
    const next = at < 0 ? 0 : (at + dir + enabled.length) % enabled.length;
    setActive(enabled[next]);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.nativeEvent.stopPropagation();
      close(true);
    } else if (e.key === 'Tab') {
      close(false);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      step(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      setActive(e.key === 'Home' ? enabled[0] : enabled[enabled.length - 1]);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(options[active]);
    } else if (e.key.length === 1) {
      const key = e.key.toLowerCase();
      const from = enabled.indexOf(active);
      const order = [...enabled.slice(from + 1), ...enabled.slice(0, from + 1)];
      const hit = order.find((i) => options[i].label.toLowerCase().startsWith(key));
      if (hit != null) setActive(hit);
    }
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const a = buttonRef.current?.getBoundingClientRect();
      if (!a) return;
      const width = Math.min(Math.max(a.width, 160), window.innerWidth - EDGE * 2);
      const h = Math.min(listRef.current?.scrollHeight ?? 0, MAX_H);
      const below = a.bottom + GAP;
      const roomBelow = window.innerHeight - below - EDGE;
      const up = h > roomBelow && a.top - GAP - EDGE > roomBelow;
      const maxH = Math.min(MAX_H, up ? a.top - GAP - EDGE : roomBelow);
      setPos({
        left: Math.min(Math.max(EDGE, a.left), window.innerWidth - EDGE - width),
        top: up ? a.top - GAP - Math.min(h, maxH) : below,
        width,
        maxH: Math.max(maxH, 120),
      });
    };
    place();
    const onDown = (e) => {
      if (!buttonRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) {
        close(false);
      }
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, pos]);

  return (
    <>
      <button
        ref={buttonRef}
        id={id}
        type='button'
        role='combobox'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={onKeyDown}
        className={cn(
          'flex items-center gap-2 text-left disabled:cursor-default disabled:bg-grayscale-gray01 disabled:text-text-placeholder',
          open && 'border-green-deep02',
          className,
        )}
      >
        <span className='min-w-0 flex-1 truncate'>
          {(current ?? options[0])?.label ?? ''}
          {current?.sub && <span className='text-text-secondary'> · {current.sub}</span>}
        </span>
        <ChevronIcon
          aria-hidden
          className={cn(
            'h-4 w-4 shrink-0 text-icon-secondary transition-transform duration-200 ease-out motion-reduce:transition-none',
            open && 'rotate-180',
          )}
        />
      </button>
      {open &&
        ReactDOM.createPortal(
          <ul
            ref={listRef}
            id={listId}
            role='listbox'
            aria-label={ariaLabel}
            style={
              pos
                ? { left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxH }
                : { left: 0, top: 0, visibility: 'hidden' }
            }
            className='fixed z-[1100] m-0 flex list-none flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-xl border border-grayscale-gray02 bg-grayscale-gray00 p-1.5 shadow-popup-01 [scrollbar-color:#d5d2cd_transparent] [scrollbar-width:thin]'
          >
            {options.map((o, i) => (
              <Fragment key={`${o.group ?? ''}-${o.value}-${i}`}>
                {o.group && o.group !== options[i - 1]?.group && (
                  <li
                    role='presentation'
                    className='px-2.5 pb-1 pt-2 text-[12px] font-medium leading-[140%] text-text-secondary'
                  >
                    {o.group}
                  </li>
                )}
                <li
                  id={`${listId}-${i}`}
                  data-index={i}
                  role='option'
                  aria-selected={i === currentIndex}
                  aria-disabled={o.disabled || undefined}
                  onMouseEnter={() => !o.disabled && setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o)}
                  className={cn(
                    'flex min-h-9 items-center gap-2 rounded-lg px-2.5 py-1.5 text-[14px] leading-[140%] transition-colors duration-100 motion-reduce:transition-none',
                    o.disabled
                      ? 'cursor-default text-text-disable'
                      : 'cursor-pointer text-text-primary',
                    i === active && !o.disabled && 'bg-grayscale-gray02',
                    i === currentIndex && 'font-medium',
                  )}
                >
                  <span className='flex min-w-0 flex-1 flex-col'>
                    {o.label}
                    {o.sub && (
                      <span className='line-clamp-2 text-[13px] font-normal text-text-secondary [overflow-wrap:anywhere]'>
                        {o.sub}
                      </span>
                    )}
                  </span>
                  {i === currentIndex && (
                    <CheckIcon aria-hidden className='h-4 w-4 shrink-0 text-green-deep02' />
                  )}
                </li>
              </Fragment>
            ))}
          </ul>,
          document.body,
        )}
    </>
  );
};

export default TdSelect;
